import React from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const CourseCharts = ({ course }) => {
    // 1. Data for Completetion Pie Chart
    const completionData = [
        { name: 'Tugatilgan', value: course.completed_lessons, color: '#2ecc71' },
        { name: 'Qolgan', value: course.total_lessons - course.completed_lessons, color: '#e74c3c' }
    ];

    // 2. Data for Test Results (Topic wise)
    const testData = course.topics.map((t, index) => ({
        name: `${index + 1}-dars`,
        ball: t.ball,
        amaliy: t.has_amaliy ? (t.amaliy_ball / 20) : 0, // Normalize 100 to 5
        has_amaliy: t.has_amaliy,
        max: 5, // Actually total max is 10 now
        full_name: t.nomi
    }));

    // 3. Data for Time Spent (Video vs Test)
    const timeData = course.topics.map((t, index) => ({
        name: `${index + 1}-dars`,
        video: t.video_vaqt,
        test: t.test_vaqt,
        full_name: t.nomi
    }));

    // 4. Radar Data (Skills/Attributes)

    // a. Test Natijasi (Penalty for attempts)
    const totalAttempts = course.topics.reduce((acc, t) => acc + (t.urinishlar || 1), 0);
    const avgAttempts = totalAttempts / (course.completed_lessons || 1);
    const avgScore = course.test_results.total_score / (course.completed_lessons || 1);
    // Score formula: (AvgBall * 2) but reduced by attempts. Ideal is 1 attempt.
    // Logic: 5 ball * 2 = 10. If 2 attempts, maybe 8? 
    // Let's use: (AvgBall * 2) - ((AvgAttempts - 1) * 1.5). Cap at 0.
    const testPerformance = Math.max(0, (avgScore * 2) - ((Math.max(1, avgAttempts) - 1) * 1.5));

    // b. Faollik (Daily Consistency)
    // Count unique days from 'sana'
    const uniqueDays = new Set(
        course.topics
            .filter(t => t.sana)
            .map(t => t.sana.split(' ')[0]) // Assuming 'YYYY-MM-DD HH:MM:SS'
    ).size;
    // Reward consistency: 1 point per active day, cap at 10 (or maybe mapped to total lessons?)
    // Let's say 5 days = 10 points? Or just raw days count capped at 10 for short term?
    // Let's make it relative to completed lessons to show "consistent progress".
    // Actually user said "at least 1 lesson per day". So UniqueDays is the key.
    const consistencyScore = Math.min(uniqueDays * 2, 10);

    // c. Video (Izlanuvchanlik) - More video = better (user request)
    const avgVideoTime = course.time_spent.video_watched_minutes / (course.completed_lessons || 1);
    const videoScore = Math.min(avgVideoTime / 2, 10); // 20 mins avg = 10 points

    // d. Amaliyot (Practical)
    const totalPracticalPossible = course.topics.filter(t => t.has_amaliy).length * 5;
    const totalPracticalEarned = course.topics.reduce((acc, t) => acc + (t.has_amaliy ? (t.amaliy_ball / 20) : 0), 0);
    const practicalPerformance = totalPracticalPossible > 0 ? (totalPracticalEarned / totalPracticalPossible) * 10 : 0;

    const radarData = [
        { subject: 'O\'zlashtirish', A: (course.progress / 10), fullMark: 10 },
        { subject: 'Test Natijasi', A: testPerformance, fullMark: 10 },
        { subject: 'Amaliyot', A: practicalPerformance, fullMark: 10 },
        { subject: 'Faollik', A: consistencyScore, fullMark: 10 },
        { subject: 'Izlanish', A: videoScore, fullMark: 10 },
        { subject: 'Tezlik', A: Math.min((100 - (course.time_spent.test_taking_minutes / 10)), 10), fullMark: 10 },
    ];

    return (
        <div className="charts-container" style={{ padding: '20px', overflowY: 'auto' }}>


            <div className="charts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px' }}>

                {/* O'zlashtirish Diagrammasi */}
                <div className="chart-card" style={cardStyle}>
                    <h4>O'zlashtirish Ko'rsatkichi</h4>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={completionData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {completionData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <p style={{ textAlign: 'center', fontWeight: 'bold' }}>{course.progress}% Tugatildi</p>
                </div>

                {/* Radar Diagramma */}
                <div className="chart-card" style={cardStyle}>
                    <h4>Umumiy Salohiyat</h4>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey="subject" tick={{ fill: '#333', fontSize: 12 }} />
                                <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: '#666', fontSize: 10 }} />
                                <Radar name="Sizning natijangiz" dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                                <Legend wrapperStyle={{ color: '#333' }} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Test Natijalari (Bar Chart) */}
                <div className="chart-card" style={{ ...cardStyle, gridColumn: '1 / -1' }}>
                    <h4>Mavzular Bo'yicha Test Natijalari</h4>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <BarChart data={testData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" tick={{ fill: '#333', fontSize: 12 }} />
                                <YAxis domain={[0, 10]} tick={{ fill: '#333', fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ color: '#333' }} />
                                <Bar dataKey="ball" name="Test Ball" fill="#3498db" stackId="a" radius={[0, 0, 5, 5]} />
                                <Bar dataKey="amaliy" name="Amaliyot" fill="#e67e22" stackId="a" radius={[5, 5, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Vaqt Taqsimoti (Area Chart) */}
                <div className="chart-card" style={{ ...cardStyle, gridColumn: '1 / -1' }}>
                    <h4>Vaqt Taqsimoti (Daqiqa)</h4>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <AreaChart data={timeData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" tick={{ fill: '#333', fontSize: 12 }} />
                                <YAxis tick={{ fill: '#333', fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ color: '#333' }} />
                                <Area type="monotone" dataKey="video" name="Video Ko'rish" stackId="1" stroke="#8884d8" fill="#8884d8" />
                                <Area type="monotone" dataKey="test" name="Test Ishlash" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip" style={{ background: '#fff', padding: '10px', border: '1px solid #ccc', borderRadius: '5px' }}>
                <p className="label" style={{ fontWeight: 'bold' }}>{label}</p>
                {payload.length > 0 && payload[0].payload.full_name && (
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>{payload[0].payload.full_name}</p>
                )}
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color }}>
                        {entry.name}: {entry.value}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

const cardStyle = {
    background: '#fff',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    border: '1px solid #eee'
};

export default CourseCharts;
